import { useEffect, useState } from 'react';

/*
  Franja de logos de proveedores (página de inicio, debajo de "Productos destacados").

  Los logos se administran en: Admin > Banners > "Logos de proveedores".
  Si no hay logos activos o la consulta falla, se muestran los de respaldo
  (public/logos/), así la franja nunca queda vacía.
*/

type Logo = { src: string; nombre: string };

const RESPALDO: { archivo: string; nombre: string }[] = [
  // Principales (las del "Sobre nosotros")
  { archivo: 'lth', nombre: 'LTH' },
  { archivo: 'max-energy', nombre: 'Max Energy' },
  { archivo: 'gonher', nombre: 'Gonher' },
  { archivo: 'star-power', nombre: 'Star Power' },
  { archivo: 'velox', nombre: 'Velox' },
  { archivo: 'velox-max', nombre: 'Velox Max' },
  // Resto de proveedores
  { archivo: 'optima', nombre: 'Optima' },
  { archivo: 'raider', nombre: 'Raider' },
  { archivo: 'texabat', nombre: 'TexaBat' },
  { archivo: 'bull-battery', nombre: 'Bull Battery' },
  { archivo: 'tnt', nombre: 'TNT' },
  { archivo: 'truckstar', nombre: 'Truckstar' },
  { archivo: 'bosch', nombre: 'Bosch' },
  { archivo: 'record', nombre: 'Record' },
  { archivo: 'vision', nombre: 'Vision' },
  { archivo: 'deka', nombre: 'Deka' },
  { archivo: 'deka-marine-master', nombre: 'Deka Marine Master' },
  { archivo: 'bost', nombre: 'Bost' },
  { archivo: 'tdh', nombre: 'TDH' },
  { archivo: 'ecovolt', nombre: 'Ecovolt' },
  { archivo: 'aokly', nombre: 'Aokly' },
  { archivo: 'power-sonic', nombre: 'Power-Sonic' },
];

const ESTILOS = `
.tb-marcas {
  position: relative;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);
  mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);
}
.tb-marcas-pista {
  display: flex;
  width: max-content;
  animation: tb-desliza 60s linear infinite;   /* la duracion real se calcula segun la cantidad */
}
.tb-marcas:hover .tb-marcas-pista,
.tb-marcas:active .tb-marcas-pista { animation-play-state: paused; }
.tb-marca {
  flex: 0 0 auto;
  width: 170px;
  height: 72px;
  margin: 0 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tb-marca img {
  max-width: 140px;
  max-height: 56px;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform .2s;
}
.tb-marca img:hover { transform: scale(1.08); }
@keyframes tb-desliza {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
@media (max-width: 640px) {
  .tb-marca { width: 120px; height: 56px; margin: 0 8px; }
  .tb-marca img { max-width: 100px; max-height: 40px; }
}
@media (prefers-reduced-motion: reduce) {
  .tb-marcas { -webkit-mask-image: none; mask-image: none; }
  .tb-marcas-pista { animation: none; flex-wrap: wrap; width: auto; justify-content: center; }
  .tb-marca[aria-hidden="true"] { display: none; }
}
`;

const LOGOS_RESPALDO: Logo[] = RESPALDO.map(l => ({ src: `/logos/${l.archivo}.webp`, nombre: l.nombre }));

export default function MarcasProveedores() {
  const [logos, setLogos] = useState<Logo[] | null>(null);

  useEffect(() => {
    let listo = false;
    const usarRespaldo = () => { if (!listo) { listo = true; setLogos(LOGOS_RESPALDO); } };
    const espera = setTimeout(usarRespaldo, 5000);   // si el servidor tarda, no dejar la franja vacia
    fetch('/api/banners/?posicion=marcas')
      .then(r => (r.ok ? r.json() : []))
      .then((datos: any[]) => {
        if (listo) return;
        listo = true;
        const desdeAdmin: Logo[] = Array.isArray(datos)
          ? datos.filter(d => d && d.url).map(d => ({ src: d.url, nombre: d.titulo || 'Marca' }))
          : [];
        setLogos(desdeAdmin.length ? desdeAdmin : LOGOS_RESPALDO);
      })
      .catch(usarRespaldo)
      .then(() => clearTimeout(espera));
    return () => clearTimeout(espera);
  }, []);

  // Con pocos logos se repite la serie para llenar pantallas anchas; la lista va dos veces
  // seguidas para que la cinta de la vuelta sin saltos. La velocidad es la misma con 5 o 30 logos.
  const serie: Logo[] = [];
  if (logos) {
    const repeticiones = Math.max(1, Math.ceil(16 / logos.length));
    for (let r = 0; r < repeticiones; r++) serie.push(...logos);
  }
  const cinta = [...serie, ...serie];
  const duracion = Math.round(serie.length * 2.75);

  return (
    <section className="bg-white py-14 px-4" aria-label="Marcas que trabajamos">
      <style>{ESTILOS}</style>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-rojo text-xs font-bold tracking-widest mb-2">PROVEEDORES</div>
          <h2 className="font-condensed text-3xl md:text-5xl font-black text-azul">Marcas que trabajamos</h2>
        </div>

        <div className="tb-marcas" style={{ minHeight: 72 }}>
          {logos && (
            <div className="tb-marcas-pista" style={{ animationDuration: `${duracion}s` }}>
              {cinta.map((l, i) => {
                const copia = i >= (logos?.length ?? 0);
                return (
                  <div key={i} className="tb-marca" aria-hidden={copia ? 'true' : undefined}>
                    <img
                      src={l.src}
                      alt={copia ? '' : l.nombre}
                      title={l.nombre}
                      decoding="async"
                      draggable={false}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
